/**
 * Shared "Does your data contain labels/entities/summaries/outputs?" switch
 * copy and modes used by the upload modal and Creation Wizard.
 *
 * When the switch is OFF, the user picks unlabeled vs generate-with-AI.
 */

import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';

export const NO_LABELS_MODE_UNLABELED = 'unlabeled';
export const NO_LABELS_MODE_GENERATE_WITH_AI = 'generate_with_ai';

/** Default when the user turns the has-labels switch OFF. */
export const NO_LABELS_MODE_DEFAULT = NO_LABELS_MODE_GENERATE_WITH_AI;

/**
 * Task-aware nouns for the has-labels switch and follow-up radios.
 *
 * @param {string} [taskType]
 * @returns {{
 *   plural: string,
 *   singular: string,
 *   withoutPhrase: string,
 *   untilPhrase: string,
 * }}
 */
export function hasLabelsNouns(taskType) {
    switch (taskType) {
        case TaskType.NER:
        case TaskType.NER_WITH_RELATIONS:
        case 'EntityRecognitionResponse':
        case 'NERResponse':
            return {
                plural: 'entities',
                singular: 'entity',
                withoutPhrase: 'without entities',
                untilPhrase: 'they are annotated',
            };
        case TaskType.summarization:
        case 'Summarization':
            return {
                plural: 'summaries',
                singular: 'summary',
                withoutPhrase: 'without summaries',
                untilPhrase: 'they have a summary',
            };
        case TaskType.contentGeneration:
        case 'ContentGeneration':
            return {
                plural: 'outputs',
                singular: 'output',
                withoutPhrase: 'without outputs',
                untilPhrase: 'they have an output',
            };
        default:
            return {
                plural: 'labels',
                singular: 'label',
                withoutPhrase: 'without labels',
                untilPhrase: 'they are labeled',
            };
    }
}

/**
 * @param {string} [taskType]
 * @returns {string}
 */
export function hasLabelsSwitchLabel(taskType) {
    const { plural } = hasLabelsNouns(taskType);
    return `Does your data contain ${plural}?`;
}

/**
 * Follow-up radio options when the has-labels switch is OFF.
 *
 * @param {string} [taskType]
 * @returns {Array<{ value: string, title: string, description: string }>}
 */
export function noLabelsModeOptions(taskType) {
    const nouns = hasLabelsNouns(taskType);
    return [
        {
            value: NO_LABELS_MODE_GENERATE_WITH_AI,
            title: `Generate ${nouns.plural} with AI`,
            description: `Import ${nouns.withoutPhrase} and let AI generate ${nouns.plural} after upload.`,
        },
        {
            value: NO_LABELS_MODE_UNLABELED,
            title: `Add ${nouns.withoutPhrase}`,
            description: `Import ${nouns.withoutPhrase}. These samples stay excluded from training until ${nouns.untilPhrase}.`,
        },
    ];
}

/**
 * True when empty response column + AI path should stamp specific_ai_labeled
 * for this generative task (mirrors backend empty-column AI rule).
 *
 * @param {string} [taskType]
 * @returns {boolean}
 */
export function isGenerativeAiEmptyColumnTask(taskType) {
    return isGenerativeTask(taskType);
}

/**
 * Whether an opted-out (hasLabels=false) entry should submit AI label mappings.
 *
 * @param {Object} entry
 * @returns {boolean}
 */
export function shouldSubmitAiLabelMappings(entry) {
    return (
        entry?.hasLabels === false &&
        entry?.noLabelsMode !== NO_LABELS_MODE_UNLABELED
    );
}

/**
 * Wizard Generate-with-AI waits for Model Configuration Next to arm
 * LABEL_ONLY. Add Data and other non-wizard surfaces have no arm step, so
 * they must generate immediately.
 *
 * @param {boolean} isWizard
 * @param {boolean} wantsAiLabeling
 * @returns {boolean}
 */
export function shouldDeferLabelGeneration(isWizard, wantsAiLabeling) {
    return Boolean(isWizard && wantsAiLabeling);
}
